/* eslint-disable @typescript-eslint/no-explicit-any */
// Location: src/components/admin/content-machine/ContentMachineAdminLayout.tsx
// Purpose: Renders the canonical Content Machine admin UI extracted from the live route.
// Scope: Contains presentation JSX only; workflow state and handlers stay in the controller.

import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { motion, AnimatePresence } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Id } from '@/convex/_generated/dataModel';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { Loader } from '@/components/ui/loader';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { GeneratingDialog } from '@/components/ui/generating-dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import ReactMarkdown from 'react-markdown';
import { CopyIcon } from '@/components/ui/icons/copy-icon';
import { ActivityIcon } from '@/components/ui/icons/activity-icon';
import { SmallSlideSelector } from '@/components/ui/small-slide-selector';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { Loader2 } from 'lucide-react';
import { TEMPLATE_OPTIONS } from '@/convex/prompts/socialContent';
import { PublishSuccessDialog } from '@/components/shared/PublishSuccessDialog';
import { BrandVoicePill } from '@/components/admin/BrandVoicePill';
import { CircleChevronUpIcon } from '@/components/ui/icons/circle-chevron-up-icon';
import { CircleChevronDownIcon } from '@/components/ui/icons/circle-chevron-down-icon';
import { MiniPostAnalytics } from '@/components/shared/MiniPostAnalytics';
import type { ContentMachineProfile } from './types/contentMachine.types';

interface ContentMachineAdminLayoutProps {
	id: any;
	navigate: any;
	Confetti: any;
	contentMachineQuery: any;
	profiles: any;
	filteredProfiles: any;
	swarmPods: any;
	selectedPod: any;
	handlePodSelect: any;
	connectedProfile: any;
	showSettings: any;
	setShowSettings: any;
	handleRename: any;
	toggleActive: any;
	toast: any;
	selectedProfile: any;
	handleProfileSelect: any;
	setShowDisconnectConfirm: any;
	nextContent: any;
	setNextContent: any;
	selectedTemplate: any;
	setSelectedTemplate: any;
	randomTemplate: any;
	setRandomTemplate: any;
	buzzUrl: any;
	setBuzzUrl: any;
	showBuzz: any;
	setShowBuzz: any;
	updateShowBuzz: any;
	selectedBuzzAgent: any;
	setSelectedBuzzAgent: any;
	selectedBuzzFeedItem: any;
	setSelectedBuzzFeedItem: any;
	selectedResearchItem: any;
	setSelectedResearchItem: any;
	handleRun: any;
	isRunning: any;
	setIsRunning: any;
	pickNewRandomTemplate: any;
	userHasResearchItems: any;
	availableResearchItems: any;
	availableBuzzFeedItems: any;
	buzzTopicPreviewItem: any;
	buzzTopicPreviewTitle: any;
	buzzTopicPreviewContext: any;
	buzzTopicPreviewMeta: any;
	buzzAgents: any;
	updateBuzzAgent: any;
	contentHistoryQuery: any;
	contentHistory: any;
	historyPage: any;
	previousPage: any;
	previousTotal: any;
	handleNavigation: any;
	isUpButtonHovered: any;
	setIsUpButtonHovered: any;
	isDownButtonHovered: any;
	setIsDownButtonHovered: any;
	isAnimating: any;
	slideDirection: any;
	engineToDelete: any;
	setEngineToDelete: any;
	deleteEngine: any;
	engineToRename: any;
	setEngineToRename: any;
	updateName: any;
	showDisconnectConfirm: any;
	error: any;
	setError: any;
	userBrandVoice: any;
	selectedBrandVoice: any;
	setBrandVoiceToConfirm: any;
	isAdmin: any;
	documents: any;
	setCurrentAnalysis: any;
	setViewingBrandVoice: any;
	setShowAnalysisDialog: any;
	BRAND_VOICES: any;
	brandVoiceStyles: any;
	brentProfile: any;
	reverendProfile: any;
	showAnalysisDialog: any;
	viewingBrandVoice: any;
	currentAnalysis: any;
	brandVoiceToConfirm: any;
	setIsUpdatingBrandVoice: any;
	adminPreviewProfile: any;
	updateBrandVoice: any;
	isUpdatingBrandVoice: any;
	showDeleteContentConfirm: any;
	setShowDeleteContentConfirm: any;
	deleteContent: any;
	showPublishDialog: any;
	setShowPublishDialog: any;
	isPublishing: any;
	handlePublishNow: any;
	handleSchedule: any;
	showPublishSuccess: any;
	handlePublishSuccessClose: any;
	publishedPostUrl: any;
	publishedTemplateName: any;
	handleApprove: any;
	isApproving: any;
	approveContent: any;
	approvedContent: any;
	currentTemplate: any;
	setSelectedBrandVoice: any;
}

export function ContentMachineAdminLayout(props: ContentMachineAdminLayoutProps) {
	const {
		id,
		navigate,
		Confetti,
		contentMachineQuery,
		profiles,
		filteredProfiles,
		swarmPods,
		selectedPod,
		handlePodSelect,
		connectedProfile,
		showSettings,
		setShowSettings,
		handleRename,
		toggleActive,
		toast,
		selectedProfile,
		handleProfileSelect,
		setShowDisconnectConfirm,
		nextContent,
		setNextContent,
		selectedTemplate,
		setSelectedTemplate,
		randomTemplate,
		setRandomTemplate,
		buzzUrl,
		setBuzzUrl,
		showBuzz,
		setShowBuzz,
		updateShowBuzz,
		selectedBuzzAgent,
		setSelectedBuzzAgent,
		selectedBuzzFeedItem,
		setSelectedBuzzFeedItem,
		selectedResearchItem,
		setSelectedResearchItem,
		handleRun,
		isRunning,
		setIsRunning,
		pickNewRandomTemplate,
		userHasResearchItems,
		availableResearchItems,
		availableBuzzFeedItems,
		buzzTopicPreviewItem,
		buzzTopicPreviewTitle,
		buzzTopicPreviewContext,
		buzzTopicPreviewMeta,
		buzzAgents,
		updateBuzzAgent,
		contentHistoryQuery,
		contentHistory,
		historyPage,
		previousPage,
		previousTotal,
		handleNavigation,
		isUpButtonHovered,
		setIsUpButtonHovered,
		isDownButtonHovered,
		setIsDownButtonHovered,
		isAnimating,
		slideDirection,
		engineToDelete,
		setEngineToDelete,
		deleteEngine,
		engineToRename,
		setEngineToRename,
		updateName,
		showDisconnectConfirm,
		error,
		setError,
		userBrandVoice,
		selectedBrandVoice,
		setBrandVoiceToConfirm,
		isAdmin,
		documents,
		setCurrentAnalysis,
		setViewingBrandVoice,
		setShowAnalysisDialog,
		BRAND_VOICES,
		brandVoiceStyles,
		brentProfile,
		reverendProfile,
		showAnalysisDialog,
		viewingBrandVoice,
		currentAnalysis,
		brandVoiceToConfirm,
		setIsUpdatingBrandVoice,
		adminPreviewProfile,
		updateBrandVoice,
		isUpdatingBrandVoice,
		showDeleteContentConfirm,
		setShowDeleteContentConfirm,
		deleteContent,
		showPublishDialog,
		setShowPublishDialog,
		isPublishing,
		handlePublishNow,
		handleSchedule,
		showPublishSuccess,
		handlePublishSuccessClose,
		publishedPostUrl,
		publishedTemplateName,
		handleApprove,
		isApproving,
		approveContent,
		approvedContent,
		currentTemplate,
		setSelectedBrandVoice
	} = props;

		return (
		<div className='container mx-auto max-w-5xl'>
			<Confetti />
			<Card className='glass-morphism border-4 mb-11'>
				<CardHeader className='space-y-8'>
					<Button
						variant='outline'
						size='lg'
						onClick={() => {
							navigate('/admin/content-machine', {
								replace: true,
							});
						}}
						className='hover:bg-secondary text-2xl font-bold px-8 py-6 mb-4'
					>
						← BACK TO ALL CONTENT MACHINES
					</Button>

					<div className='space-y-4'>
						<div className='space-y-2'>
							<CardTitle className='flex items-center gap-2 text-3xl mb-6'>
								<span className='text-pink-500 font-bold'>
									CONTENT MACHINE:
								</span>
								{contentMachineQuery.name}
							</CardTitle>
							<div className='flex items-center justify-between'>
								<div className='flex items-center gap-4'>
									<span
										className={cn(
											'px-4 py-2 rounded-full text-base font-bold',
											contentMachineQuery.isActive
												? 'bg-gradient-to-b from-emerald-400 to-green-600 text-white shadow-lg'
												: 'bg-gradient-to-b from-orange-400 to-red-600 text-white shadow-lg',
										)}
									>
										{contentMachineQuery.isActive ? '🟢 ACTIVE' : '🔴 INACTIVE'}
									</span>
									<div className='flex items-center gap-2'>
										<Badge
											variant='outline'
											className={cn(
												'text-xl px-4 py-2 transition-all duration-300 shadow-lg hover:shadow-xl',
												contentMachineQuery.platform === 'twitterx'
													? 'bg-gradient-to-b from-zinc-800 via-zinc-900 to-black text-white border-zinc-700 dark:from-zinc-700 dark:via-zinc-800 dark:to-zinc-900 dark:border-zinc-600'
													: 'bg-gradient-to-b from-blue-400 to-blue-600 text-white border-blue-300 hover:from-blue-500 hover:to-blue-700',
											)}
										>
											{contentMachineQuery.platform === 'twitterx'
												? '𝕏 Content Machine'
												: 'LinkedIn Content Machine'}
										</Badge>
									</div>
									<motion.button
										onClick={() => setShowSettings(!showSettings)}
										className='w-8 h-8 rounded-full bg-purple-900 flex items-center justify-center text-white hover:bg-purple-700 transition-colors'
										animate={{
											rotate: showSettings ? 180 : 0,
										}}
										transition={{
											duration: 0.2,
											ease: 'easeInOut',
										}}
									>
										{showSettings ? '🔽' : '▶️'}
									</motion.button>
								</div>
							</div>
							{contentMachineQuery.profileId && profiles && (
								<div className='mt-4 p-4 bg-gradient-to-b from-gray-50 to-white dark:from-gray-900 dark:to-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-inner'>
									<div className='flex items-center gap-4'>
										<div className='flex items-center gap-2'>
											{contentMachineQuery.platform.toLowerCase() ===
											'linkedin' ? (
												<span className='text-lg'>💼</span>
											) : (
												<span className='text-lg'>𝕏</span>
											)}
											<div>
												<h4 className='font-semibold text-lg'>
													{
														profiles.find(
															(p: any) =>
																p._id === contentMachineQuery.profileId,
														)?.profileTitle
													}
												</h4>
												<p className='text-sm text-muted-foreground'>
													{
														profiles.find(
															(p: any) =>
																p._id === contentMachineQuery.profileId,
														)?.email
													}
												</p>
											</div>
										</div>
										<Badge
											variant='outline'
											className='bg-gradient-to-b from-emerald-400 to-green-600 text-white border-none'
										>
											🔌 Connected
										</Badge>
									</div>
								</div>
							)}
						</div>
					</div>
				</CardHeader>

				<CardContent className='space-y-12'>
					<AnimatePresence>
						{showSettings && (
							<motion.div
								initial={{ scale: 0.95, opacity: 0 }}
								animate={{ scale: 1, opacity: 1 }}
								exit={{ scale: 0.95, opacity: 0 }}
								transition={{ duration: 0.2 }}
								className='space-y-12 overflow-hidden'
							>
								{/* Action Buttons */}
								<div className='flex gap-2'>
									<Button
										size='lg'
										onClick={() =>
											contentMachineQuery &&
											handleRename(
												contentMachineQuery._id,
												contentMachineQuery.name || '',
											)
										}
										className='bg-gradient-to-b from-sky-400 to-blue-600 hover:from-sky-500 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6'
									>
										<span className='whitespace-nowrap'>✏️ Rename</span>
									</Button>
									<Button
										size='lg'
										onClick={() => setEngineToDelete(contentMachineQuery._id)}
										className='bg-gradient-to-b from-red-400 to-pink-600 hover:from-red-500 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-200 px-6'
									>
										<span className='whitespace-nowrap'>🗑️ Delete</span>
									</Button>
									<Button
										size='lg'
										onClick={async () => {
											try {
												await toggleActive({
													id: contentMachineQuery._id,
												});
												toast({
													title: contentMachineQuery.isActive
														? '🔴 Deactivated'
														: '🟢 Activated',
													description: `Content machine has been ${contentMachineQuery.isActive ? 'deactivated' : 'activated'} successfully.`,
													duration: 3000,
												});
											} catch (error) {
												toast({
													title: 'Error',
													description:
														error instanceof Error
															? error.message
															: 'Failed to toggle status',
													variant: 'destructive',
													duration: 3000,
												});
											}
										}}
										className={cn(
											'transition-all duration-200 shadow-lg hover:shadow-xl',
											contentMachineQuery.isActive
												? 'bg-gradient-to-b from-orange-400 to-red-600 hover:from-orange-500 hover:to-red-700'
												: 'bg-gradient-to-b from-emerald-400 to-green-600 hover:from-emerald-500 hover:to-green-700',
										)}
									>
										<span className='whitespace-nowrap'>
											{contentMachineQuery.isActive
												? '🔴 Deactivate'
												: '🟢 Activate'}
										</span>
									</Button>
								</div>

								{/* Social Profile Section */}
								<div className='space-y-4'>
									<div className='flex items-center justify-between'>
										<div className='space-y-1'>
											<h3 className='text-3xl font-bold'>Social Profile</h3>
											<p className='text-sm text-gray-500'>
												Select the social profile that will be used for content
												generation
											</p>
										</div>
									</div>

									<Card className='p-6'>
										<div className='space-y-4'>
											<div className='flex flex-col gap-2'>
												<label className='text-sm font-medium'>
													Select Profile
												</label>
												<div className='flex gap-2 items-center'>
													<div className='flex-1'>
														<SearchableSelect
															value={
																selectedProfile?._id.toString() ||
																contentMachineQuery.profileId?.toString() ||
																''
															}
															onValueChange={(value) => {
																const profile = filteredProfiles?.find(
																	(p: any) => p._id === value,
																);
																handleProfileSelect(
																	(profile as unknown as ContentMachineProfile) ||
																		null,
																);
															}}
															options={
																filteredProfiles?.map((profile: any) => ({
																	value: profile._id.toString(),
																	label: `${profile.profileTitle} (${profile.email})`,
																	render: () => (
																		<div className='flex items-center justify-between w-full'>
																			<div className='flex items-center gap-2'>
																				{contentMachineQuery.platform.toLowerCase() ===
																				'linkedin' ? (
																					<span className='text-lg'>💼</span>
																				) : (
																					<span className='text-lg'>𝕏</span>
																				)}
																				<span>{profile.profileTitle}</span>
																				<span className='text-gray-500'>
																					({profile.email})
																				</span>
																			</div>
																			<span className='px-2 py-1 rounded-full text-xs font-semibold bg-orange-300 text-orange-700 ml-4'>
																				LINKED
																			</span>
																		</div>
																	),
																})) || []
															}
															placeholder='Select a profile...'
														/>
													</div>
													{(selectedProfile ||
														contentMachineQuery.profileId) && (
														<Button
															onClick={() => setShowDisconnectConfirm(true)}
															className='bg-gradient-to-b from-zinc-400 to-zinc-600 hover:from-zinc-500 hover:to-zinc-700 text-white shadow-lg hover:shadow-xl transition-all duration-200'
														>
															🔌 DISCONNECT
														</Button>
													)}
												</div>
												{!filteredProfiles?.length && (
													<p className='text-sm text-orange-500'>
														{contentMachineQuery.platform.toLowerCase() ===
														'linkedin'
															? 'No profiles available for LinkedIn. Please add a LinkedIn profile first.'
															: 'No profiles available for X. Please add an X profile first.'}
													</p>
												)}
											</div>
										</div>
									</Card>
								</div>

								{/* Engagement Pod Section */}
								<div className='space-y-4'>
									<div className='flex items-center justify-between'>
										<div className='space-y-1'>
											<h3 className='text-3xl font-bold'>Engagement Pod</h3>
											<p className='text-sm text-gray-500'>
												Select the swarm pod that will engage with published
												content
											</p>
										</div>
									</div>

									<Card className='p-6'>
										<div className='space-y-4'>
											<div className='flex flex-col gap-2'>
												<label className='text-sm font-medium'>
													Select Pod
												</label>
												<div className='flex gap-2 items-center'>
													<div className='flex-1'>
														<SearchableSelect
															value={
																selectedPod?._id?.toString() ||
																contentMachineQuery.podId?.toString() ||
																''
															}
															onValueChange={(value) => {
																const pod = swarmPods?.find(
																	(pod: any) => pod._id === value,
																);
																handlePodSelect(pod || null);
															}}
															options={
																swarmPods?.map((pod: any) => ({
																	value: pod._id.toString(),
																	label: `${pod.name} (${pod.mode || 'swarm'})`,
																	render: () => (
																		<div className='flex items-center justify-between w-full'>
																			<div className='flex items-center gap-2'>
																				<span className='text-lg'>🐝</span>
																				<span>{pod.name}</span>
																				<span className='text-gray-500'>
																					({pod.mode || 'swarm'})
																				</span>
																			</div>
																			<span className='px-2 py-1 rounded-full text-xs font-semibold bg-pink-300 text-pink-700 ml-4'>
																				SUPPORTED
																			</span>
																		</div>
																	),
																})) || []
															}
															placeholder='Select an engagement pod...'
														/>
													</div>
													{(selectedPod || contentMachineQuery.podId) && (
														<Button
															onClick={() => handlePodSelect(null)}
															className='bg-gradient-to-b from-zinc-400 to-zinc-600 hover:from-zinc-500 hover:to-zinc-700 text-white shadow-lg hover:shadow-xl transition-all duration-200'
														>
															🔌 DISCONNECT
														</Button>
													)}
												</div>
												{!swarmPods?.length && (
													<p className='text-sm text-orange-500'>
														{/^(x|twitter|twitterx)$/i.test(
															String(contentMachineQuery.platform || ''),
														)
															? 'No compatible swarm pods are available for this machine. Create an X swarm pod first.'
															: 'No compatible swarm pods are available for this machine yet. Content Machine pod attachment currently supports X swarm pods only.'}
													</p>
												)}
											</div>
										</div>
									</Card>
								</div>
							</motion.div>
						)}
					</AnimatePresence>
					<div className='h-px bg-gray-200 dark:bg-gray-800 my-16' />
					{/* Content Generation Area */}
					<div className='space-y-6'>
						<AnimatePresence mode='wait'>
							{nextContent ? (
								<motion.div
									key='preview'
									initial={{ opacity: 0, height: 0 }}
									animate={{ opacity: 1, height: 'auto' }}
									exit={{ opacity: 0, height: 0 }}
									transition={{ duration: 0.2 }}
								>
									<div className='flex flex-col gap-2 mb-4'>
										<div className='flex items-center justify-between'>
											<h3 className='text-2xl font-bold'>
												Generated Content Preview
											</h3>
											<Button
												variant='outline'
												size='sm'
												className='flex items-center gap-2'
												onClick={() => {
													navigator.clipboard.writeText(nextContent);
													toast({
														title: '🔥 Content Copied 🔥',
														description:
															'The content has been copied to your clipboard.',
														duration: 3000,
													});
												}}
											>
												<CopyIcon />
												COPY
											</Button>
										</div>
										<div className='flex items-center gap-2'>
											{contentMachineQuery?.brandVoice?.id && (
												<Badge
													variant='outline'
													className='bg-gradient-to-b from-purple-400 to-purple-600 text-white px-3 py-1 w-fit'
												>
													{contentMachineQuery.brandVoice.id === 'prometheus' &&
														'🔥'}
													{contentMachineQuery.brandVoice.id === 'reverend' &&
														'🎭'}
													{contentMachineQuery.brandVoice.id === 'brent' &&
														'💼'}
													{contentMachineQuery.brandVoice.id === 'user' && '✨'}
													{contentMachineQuery.brandVoice.id === 'prometheus' &&
														'Prometheus'}
													{contentMachineQuery.brandVoice.id === 'reverend' &&
														'The Reverend'}
													{contentMachineQuery.brandVoice.id === 'brent' &&
														'Brent Ecomm Master'}
													{contentMachineQuery.brandVoice.id === 'user' &&
														'My Brand Voice'}
												</Badge>
											)}
											{selectedTemplate === 'none' ? (
												<Badge
													variant='outline'
													className='bg-gradient-to-b from-gray-400 to-gray-600 text-white px-3 py-1 w-fit'
												>
													No Template
												</Badge>
											) : (
												<Badge
													variant='outline'
													className='bg-gradient-to-b from-blue-400 to-blue-600 text-white px-3 py-1 w-fit'
												>
													{TEMPLATE_OPTIONS.find(
														(opt) =>
															opt.value ===
															(selectedTemplate || randomTemplate),
													)?.label || 'Random Template'}
												</Badge>
											)}
										</div>
									</div>
									<Card className='p-6'>
										<div className='space-y-4'>
											<Textarea
												value={nextContent}
												onChange={(e) => setNextContent(e.target.value)}
												className={cn(
													'text-lg resize-y whitespace-pre-wrap break-words',
													'after:content-[""] after:absolute after:bottom-2 after:right-2 after:w-4 after:h-4 after:cursor-nwse-resize after:bg-gradient-to-br after:from-gray-300 after:to-gray-400 after:opacity-50 after:rounded-br-md hover:after:opacity-100',
													contentMachineQuery.platform === 'linkedin'
														? 'min-h-[800px]'
														: 'min-h-[400px]',
												)}
												style={{
													whiteSpace: 'pre-wrap',
													wordBreak: 'break-word',
													overflowWrap: 'break-word',
												}}
												wrap='soft'
												placeholder='Generated content will appear here...'
											/>
											{showBuzz && buzzUrl && (
												<div className='flex items-center gap-2 text-sm text-gray-500 mb-2'>
													<span className='font-medium'>Buzz Source:</span>
													<a
														href={buzzUrl}
														target='_blank'
														rel='noopener noreferrer'
														className='text-blue-500 hover:text-blue-700 underline font-mono truncate max-w-[300px]'
														title={buzzUrl}
													>
														{buzzUrl
															.replace(/^https?:\/\/(www\.)?/, '')
															.substring(0, 40) + '...'}
													</a>
												</div>
											)}
											<div className='flex justify-end gap-2'>
												<Button
													variant='outline'
													onClick={() => setShowDeleteContentConfirm(true)}
													className='bg-gradient-to-b from-red-400 to-red-600 hover:from-red-500 hover:to-red-700 text-white border-0'
												>
													<span className='flex items-center gap-2'>
														🗑️ Delete
													</span>
												</Button>
												<Button
													onClick={async () => {
														try {
															await approveContent({
																id: contentMachineQuery._id,
																content: nextContent,
																buzzUrl: buzzUrl || undefined,
															});
															setNextContent('');
															setBuzzUrl('');
															if (
																selectedTemplate === '' ||
																selectedTemplate === 'random'
															) {
																pickNewRandomTemplate();
															}
															toast({
																title: '💾 Saved',
																description:
																	'Content has been saved successfully.',
																duration: 3000,
															});
														} catch (error) {
															console.error('Error saving content:', error);
															toast({
																title: 'Error',
																description:
																	error instanceof Error
																		? error.message
																		: 'Failed to save content',
																variant: 'destructive',
																duration: 3000,
															});
														}
													}}
													className='bg-gradient-to-b from-blue-400 to-blue-600 hover:from-blue-500 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all duration-200'
												>
													<span className='flex items-center gap-2'>
														💾 Save
													</span>
												</Button>
												<Button
													onClick={handleApprove}
													disabled={isApproving}
													className='bg-gradient-to-b from-green-400 to-green-600 hover:from-green-500 hover:to-green-700 text-white'
												>
													{isApproving ? (
														<>
															<Loader2 className='mr-2 h-4 w-4 animate-spin' />
															Saving...
														</>
													) : (
														'💾 Save & Publish'
													)}
												</Button>
											</div>
										</div>
									</Card>
								</motion.div>
							) : (
								<motion.div
									key='generate'
									initial={{ opacity: 0, y: 20 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -20 }}
									transition={{ duration: 0.2 }}
									className='flex flex-col items-center justify-center py-4'
								>
									<Button
										size='lg'
										onClick={handleRun}
										disabled={
											isRunning ||
											!contentMachineQuery.profileId ||
											profiles === undefined ||
											(showBuzz &&
												!selectedBuzzAgent &&
												!contentMachineQuery?.agentType)
										}
										className={cn(
											'text-white shadow-xl hover:shadow-2xl transition-all duration-300 text-xl py-16 px-16 rounded-2xl transform hover:scale-105 mb-4',
											contentMachineQuery.platform === 'linkedin'
												? 'bg-gradient-to-b from-blue-400 via-blue-500 to-blue-700 hover:from-blue-500 hover:via-blue-600 hover:to-blue-800'
												: 'bg-gradient-to-b from-violet-400 via-purple-500 to-purple-700 hover:from-violet-500 hover:via-purple-600 hover:to-purple-800',
										)}
									>
										<div className='flex flex-col items-center gap-1'>
											<span className='whitespace-nowrap flex items-center gap-3 text-2xl font-bold'>
												{isRunning
													? '⏳ Generating...'
													: profiles === undefined
														? '⌛ Loading Profiles...'
														: showBuzz &&
															  !selectedBuzzAgent &&
															  !contentMachineQuery?.agentType
															? 'Select an Agent First'
															: `✨ Generate ${
																	contentMachineQuery.platform === 'linkedin'
																		? 'LinkedIn Post'
																		: '𝕏 Post'
																}`}
											</span>
											{!isRunning && contentMachineQuery?.brandVoice?.id && (
												<span className='text-base text-white/90'>
													<span className='opacity-75'>Brand Voice: </span>
													{contentMachineQuery.brandVoice.id === 'prometheus' &&
														'🔥'}
													{contentMachineQuery.brandVoice.id === 'reverend' &&
														'🎭'}
													{contentMachineQuery.brandVoice.id === 'brent' &&
														'💼'}
													{contentMachineQuery.brandVoice.id === 'user' && '✨'}
													{contentMachineQuery.brandVoice.id === 'prometheus' &&
														'Prometheus'}
													{contentMachineQuery.brandVoice.id === 'reverend' &&
														'The Reverend'}
													{contentMachineQuery.brandVoice.id === 'brent' &&
														'Brent Ecomm Master'}
													{contentMachineQuery.brandVoice.id === 'user' &&
														'My Brand Voice'}
												</span>
											)}
										</div>
									</Button>

									<div className='w-full max-w-[320px] mx-auto mt-8'>
										<div className='flex items-center gap-2'>
											<div className='flex-1 [&_[data-radix-select-trigger]]:border-2 [&_[data-radix-select-trigger]]:border-gray-200 dark:[&_[data-radix-select-trigger]]:border-gray-700 [&_[data-radix-select-trigger]]:hover:border-gray-300 dark:[&_[data-radix-select-trigger]]:hover:border-gray-600 shadow-[0_0_20px_rgba(236,72,153,0.3)]'>
												<SearchableSelect
													value={selectedTemplate || 'random'}
													onValueChange={(value) => {
														if (value === 'random') {
															setSelectedTemplate('');
															const randomIndex = Math.floor(
																Math.random() * TEMPLATE_OPTIONS.length,
															);
															setRandomTemplate(
																TEMPLATE_OPTIONS[randomIndex].value,
															);
														} else {
															setSelectedTemplate(value);
														}
													}}
													options={[
														{
															value: 'none',
															label: '❌ No Template',
														},
														{
															value: 'random',
															label: '🎲 Random Template',
														},
														...TEMPLATE_OPTIONS.map((opt) => ({
															value: opt.value,
															label: `${opt.label}`,
														})),
													]}
													placeholder='Select Social Template'
												/>
											</div>
											<TooltipProvider>
												<Tooltip delayDuration={0}>
													<TooltipTrigger asChild>
														<div className='flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-b from-pink-300 via-pink-500 to-pink-700 text-white text-xs font-semibold cursor-help'>
															❔
														</div>
													</TooltipTrigger>
													<TooltipContent side='top'>
														<p className='text-sm'>
															Select from over 100 proven social content
															templates.
															<br />
															🔥 Prometheus chooses one at random by default.
														</p>
													</TooltipContent>
												</Tooltip>
											</TooltipProvider>
										</div>
										<p className='text-base text-gray-700 dark:text-gray-300 mt-4 text-center'>
											{selectedTemplate === 'none' ? (
												<span className='inline-flex items-center gap-1.5 whitespace-nowrap'>
													Social Template:{' '}
													<span className='inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-0.5'>
														<span className='font-semibold'>None</span>
													</span>
												</span>
											) : selectedTemplate ? (
												<span className='inline-flex items-center gap-1.5 whitespace-nowrap'>
													Social Template:{' '}
													<span className='inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-0.5'>
														<span className='font-semibold'>
															{
																TEMPLATE_OPTIONS.find(
																	(opt) => opt.value === selectedTemplate,
																)?.label
															}
														</span>
													</span>
												</span>
											) : (
												<span className='inline-flex items-center gap-1.5 whitespace-nowrap'>
													Social Template:{' '}
													<span className='inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-0.5'>
														<span className='font-semibold'>
															{
																TEMPLATE_OPTIONS.find(
																	(opt) => opt.value === randomTemplate,
																)?.label
															}
														</span>
													</span>
												</span>
											)}
										</p>
									</div>

									{!contentMachineQuery.profileId && (
										<p className='text-orange-500 mt-4'>
											Please connect a profile first to generate content
										</p>
									)}
								</motion.div>
							)}
						</AnimatePresence>

						{/* The Buzz Toggle Swi*/}
						{contentMachineQuery.profileId && !nextContent && (
							<div className='mt-8 flex flex-col items-center justify-center gap-2'>
								<SmallSlideSelector
									value={showBuzz ? 'on' : 'off'}
									onChange={async (value) => {
										const newShowBuzz = value === 'on';
										setShowBuzz(newShowBuzz);
										// Persist to database
										if (contentMachineQuery?._id) {
											try {
												await updateShowBuzz({
													machineId: contentMachineQuery._id,
													showBuzz: newShowBuzz,
												});
											} catch (error) {
												console.error('Failed to update buzz toggle:', error);
												// Revert on error
												setShowBuzz(!newShowBuzz);
											}
										}
									}}
									options={[
										{
											value: 'off',
											icon: <ActivityIcon isHovered={false} />,
											className:
												'bg-gradient-to-b from-gray-300 via-gray-500 to-gray-700',
										},
										{
											value: 'on',
											icon: <ActivityIcon isHovered={true} />,
											className:
												'bg-gradient-to-b from-indigo-400 via-indigo-600 to-indigo-900',
										},
									]}
									className='w-full max-w-[120px]'
									activeColor={showBuzz ? 'indigo' : 'gray'}
								/>
								<div className='flex items-center gap-1.5'>
									<span
										className={cn(
											'text-sm transition-colors',
											showBuzz
												? 'text-pink-600 font-bold'
												: 'text-gray-400 font-medium',
										)}
									>
										The Buzz
									</span>
									<TooltipProvider>
										<Tooltip delayDuration={0}>
											<TooltipTrigger asChild>
												<div className='flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-b from-pink-300 via-pink-500 to-pink-700 text-white text-xs font-semibold cursor-help'>
													❔
												</div>
											</TooltipTrigger>
											<TooltipContent side='bottom'>
												<p className='text-sm'>
													The Buzz toggle allows Prometheus to
													<br />
													use trending news to create content
												</p>
											</TooltipContent>
										</Tooltip>
									</TooltipProvider>
								</div>

								{/* Show Agent selector when buzz is ON */}
								{showBuzz && (
									<div className='mt-4 w-full max-w-[320px]'>
										<SearchableSelect
											value={
												selectedBuzzAgent ||
												(contentMachineQuery?.agentType === 'buzz' &&
												contentMachineQuery?.buzzAgentId
													? `buzz_${contentMachineQuery.buzzAgentId}`
													: '') ||
												(contentMachineQuery?.agentType === 'research'
													? 'research'
													: '') ||
												(() => {
													// Find the "🔥 Prometheus" agent as default
													const prometheusAgent = buzzAgents?.find(
														(agent: any) => agent.name === '🔥 Prometheus',
													);
													return prometheusAgent
														? `buzz_${prometheusAgent._id}`
														: '';
												})()
											}
											onValueChange={async (value) => {
												setSelectedBuzzAgent(value);
												// Reset topic selection when agent changes
												setSelectedBuzzFeedItem('auto');
												setSelectedResearchItem('auto');
												if (contentMachineQuery) {
													try {
														// Determine agent type from value prefix
														const isBuzzAgent = value?.startsWith('buzz_');
														const isResearchAgent = value === 'research';

														if (isBuzzAgent) {
															const actualId = value.replace('buzz_', '');
															await updateBuzzAgent({
																machineId: contentMachineQuery._id,
																buzzAgentId: actualId
																	? (actualId as Id<'buzzAgents'>)
																	: null,
																agentType: 'buzz',
																agentId: actualId,
															});
														} else if (isResearchAgent) {
															// For research agent, we use a generic identifier
															await updateBuzzAgent({
																machineId: contentMachineQuery._id,
																buzzAgentId: null,
																agentType: 'research',
																agentId: 'research', // Generic identifier for research agent type
															});
														} else {
															await updateBuzzAgent({
																machineId: contentMachineQuery._id,
																buzzAgentId: null,
																agentType: null,
																agentId: null,
															});
														}

														// Find the selected agent to get its name
														const selectedAgent = isBuzzAgent
															? buzzAgents?.find(
																	(a: any) => `buzz_${a._id}` === value,
																)
															: null;
														const agentName =
															selectedAgent?.name ||
															(isResearchAgent ? 'Research' : 'Unknown');

														toast({
															title: value
																? '🤖 Agent Connected'
																: '🤖 Agent Disconnected',
															description: value
																? `Successfully connected ${agentName} agent`
																: 'Agent disconnected',
															duration: 3000,
														});
													} catch (error) {
														toast({
															title: 'Error',
															description:
																error instanceof Error
																	? error.message
																	: 'Failed to update agent',
															variant: 'destructive',
															duration: 3000,
														});
													}
												}
											}}
											options={[
												// Buzz agents (including the "🔥 Prometheus" agent)
												...(buzzAgents?.map((agent: any) => ({
													value: `buzz_${agent._id}`,
													label: agent.name,
													render: () => (
														<div className='flex items-center justify-between w-full'>
															<div className='flex items-center gap-2'>
																<span className='text-lg'>
																	{agent.name === '🔥 Prometheus' ? '🔥' : '🐝'}
																</span>
																<span>{agent.name}</span>
															</div>
															{/* {agent.isActive && (
																	<span className='ml-4 px-2 py-1 rounded-full text-xs font-semibold bg-green-300 text-green-700'>
																		ACTIVE
																	</span>
																)} */}
														</div>
													),
												})) || []),
												// Single Research Agent option (show if user has any research items)
												...(userHasResearchItems?.page &&
												userHasResearchItems.page.length > 0
													? [
															{
																value: 'research',
																label: 'Research Agent',
																render: () => (
																	<div className='flex items-center justify-between w-full'>
																		<div className='flex items-center gap-2'>
																			<span className='text-lg'>🔬</span>
																			<span>Research Agent</span>
																		</div>
																		<span className='ml-4 text-sm text-gray-400'>
																			{availableResearchItems?.length || 0}{' '}
																			available
																		</span>
																	</div>
																),
															},
														]
													: []),
											]}
											placeholder='Select an Agent...'
										/>
										{!buzzAgents?.length &&
											(!userHasResearchItems?.page ||
												userHasResearchItems.page.length === 0) && (
												<p className='text-sm text-orange-500 mt-2 text-center'>
													No agents available. Create a Buzz Agent or Research
													Agent for this machine.
												</p>
											)}

										{showBuzz &&
											selectedBuzzAgent &&
											selectedBuzzAgent.startsWith('buzz_') &&
											availableBuzzFeedItems &&
											availableBuzzFeedItems.length > 0 && (
												<div className='mt-4 w-full max-w-[320px]'>
													<div className='flex flex-col gap-2'>
														<label className='text-sm font-medium text-gray-300'>
															Select Buzz Topic
														</label>
														<div className='[&_[data-radix-select-trigger]]:overflow-hidden border border-gray-500 rounded-md'>
															<SearchableSelect
																value={selectedBuzzFeedItem}
																onValueChange={(value) => {
																	setSelectedBuzzFeedItem(value);
																}}
																options={[
																	{
																		value: 'auto',
																		label: '🎲 Auto-select next topic',
																	},
																	...availableBuzzFeedItems.map((item: any) => {
																		// Truncate the label if it's too long
																		const fullText =
																			item.headline || item.title || 'Untitled';
																		const truncatedLabel =
																			fullText.length > 33
																				? fullText.substring(0, 33) + '...'
																				: fullText;
																		return {
																			value: item._id,
																			label: truncatedLabel,
																		};
																	}),
																]}
																placeholder='Select topic or auto-select...'
															/>
														</div>
														{buzzTopicPreviewItem && (
															<div className='mt-3'>
																<p className='text-sm text-pink-500 font-semibold'>
																	Buzz Topic Preview
																</p>
																<div className='mt-2 space-y-2 rounded-md bg-gray-900/50 p-3'>
																	<p className='text-sm font-medium text-gray-100'>
																		{buzzTopicPreviewTitle}
																	</p>
																	<p className='text-xs leading-relaxed text-gray-300'>
																		{buzzTopicPreviewContext}
																	</p>
																	{buzzTopicPreviewMeta && (
																		<p className='text-[11px] text-gray-500'>
																			{buzzTopicPreviewMeta}
																		</p>
																	)}
																</div>
															</div>
														)}
													</div>
												</div>
											)}

										{showBuzz &&
											selectedBuzzAgent === 'research' &&
											availableResearchItems &&
											availableResearchItems.length > 0 && (
												<div className='mt-4 w-full max-w-[320px]'>
													<div className='flex flex-col gap-2'>
														<label className='text-sm font-medium text-gray-300'>
															Select Research Item
														</label>
														<div className='[&_[data-radix-select-trigger]]:overflow-hidden border border-gray-500 rounded-md'>
															<SearchableSelect
																value={selectedResearchItem}
																onValueChange={(value) => {
																	setSelectedResearchItem(value);
																}}
																options={[
																	{
																		value: 'auto',
																		label: '🎲 Auto-select (Next Available)',
																	},
																	...availableResearchItems.map((item: any) => {
																		// Truncate the label if it's too long
																		const fullText = item.subject || 'Untitled';
																		const truncatedLabel =
																			fullText.length > 33
																				? fullText.substring(0, 33) + '...'
																				: fullText;
																		return {
																			value: item._id,
																			label: truncatedLabel,
																			render: () => (
																				<div className='flex items-center justify-between w-full'>
																					<span className='truncate'>
																						{truncatedLabel}
																					</span>
																					<span className='text-xs text-gray-500 ml-2'>
																						{item.status === 'completed'
																							? '✅'
																							: '⏳'}
																					</span>
																				</div>
																			),
																		};
																	}),
																]}
																placeholder='Select research item or auto-select...'
															/>
														</div>
														{selectedResearchItem &&
															selectedResearchItem !== 'auto' && (
																<div className='mt-3'>
																	<p className='text-sm text-purple-500 font-semibold'>
																		Research Item
																	</p>
																	{availableResearchItems && (
																		<div className='mt-2 p-2 bg-gray-900/50 rounded-md'>
																			<p className='text-xs text-gray-300 line-clamp-2'>
																				{availableResearchItems.find(
																					(item: any) =>
																						item._id === selectedResearchItem,
																				)?.subject || 'Selected item'}
																			</p>
																		</div>
																	)}
																</div>
															)}
													</div>
												</div>
											)}
									</div>
								)}
							</div>
						)}
					</div>
					<div className='h-px bg-gray-200 dark:bg-gray-800 my-16' />

					<div className='space-y-6'>
						<div className='flex justify-between items-center mb-8'>
							<div className='space-y-1'>
								<h3 className='text-3xl font-bold'>Latest Content:</h3>
							</div>
							<Button
								variant='outline'
								onClick={() => navigate(`/admin/content-machine/${id}/history`)}
								className='relative group bg-gradient-to-br from-orange-500/90 via-orange-600/90 to-orange-700/90 hover:from-orange-600 hover:via-orange-700 hover:to-orange-800 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap h-[60px] px-8'
							>
								<span className='flex items-center gap-1.5 text-xl'>
									<span className='relative'>
										<span className='absolute -left-1 -top-1 animate-ping opacity-75'>
											🕒
										</span>
										🕒
									</span>
									Content History
								</span>
							</Button>
						</div>
						<div className='flex'>
							<div className='flex flex-col items-center justify-center gap-4 mr-4'>
								<button
									onClick={() => handleNavigation('up')}
									disabled={historyPage === 0 || !contentHistoryQuery}
									className='flex items-center justify-center'
									onMouseEnter={() => setIsUpButtonHovered(true)}
									onMouseLeave={() => setIsUpButtonHovered(false)}
								>
									<CircleChevronUpIcon isHovered={isUpButtonHovered} />
								</button>
								<div className='flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-pink-500/10 via-pink-500/20 to-pink-500/10 rounded-full'>
									<div className='text-pink-500 font-medium'>
										{contentHistoryQuery?.currentPage || previousPage || 1} of{' '}
										{contentHistoryQuery?.totalPages || previousTotal || 1}
									</div>
								</div>
								<button
									onClick={() => handleNavigation('down')}
									disabled={
										!contentHistory ||
										contentHistory.length < 3 ||
										!contentHistoryQuery?.hasMore
									}
									className='flex items-center justify-center'
									onMouseEnter={() => setIsDownButtonHovered(true)}
									onMouseLeave={() => setIsDownButtonHovered(false)}
								>
									<CircleChevronDownIcon isHovered={isDownButtonHovered} />
								</button>
							</div>
							<div className='flex-1 overflow-hidden'>
								<div className='h-[calc(3.5*180px)] overflow-y-auto pr-4 space-y-4 scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent'>
									{isAnimating || !contentHistoryQuery ? (
										<div className='h-full flex items-center justify-center'>
											<Loader
												message='LOADING HISTORY...'
												className='scale-50'
											/>
										</div>
									) : (
										<motion.div
											className='space-y-4'
											initial={false}
											animate={{
												y: isAnimating
													? slideDirection === 'up'
														? '100%'
														: '-100%'
													: 0,
												opacity: isAnimating ? 0 : 1,
											}}
											transition={{
												type: 'spring',
												stiffness: 100,
												damping: 20,
												mass: 1,
											}}
										>
											{contentHistory && contentHistory.length > 0 ? (
												contentHistory.map(
													(
														item: {
															_id: Id<'contentHistory'>;
															content: string;
															createdAt: string;
															metadata?: {
																postUrl?: string;
																templateName?: string;
																postId?: string;
															};
															brandVoice?: {
																id: string;
																profileId?: Id<'profiles'>;
															};
														},
														index: number,
													) => (
														<motion.div
														key={String(item._id)}
															initial={{
																opacity: 0,
																y: 20,
															}}
															animate={{
																opacity: 1,
																y: 0,
															}}
															exit={{
																opacity: 0,
																y: -20,
															}}
															transition={{
																duration: 0.4,
																ease: [0.4, 0, 0.2, 1],
																delay: index * 0.15,
															}}
															className='flex flex-col gap-4 p-4 rounded-lg bg-gray-900/50 backdrop-blur-sm shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer hover:bg-gray-900/60 border border-gray-700/50'
															onClick={() =>
																navigate(`/admin/content-machine/${id}/history`)
															}
														>
															<div className='flex items-center gap-2 text-lg text-gray-400'>
																<Badge
																	variant='outline'
																	className={cn(
																		'px-3 py-1',
																		item.metadata?.postUrl
																			? 'bg-gradient-to-b from-pink-400 to-pink-600 text-white'
																			: 'bg-gradient-to-b from-orange-500 to-orange-700 text-white',
																	)}
																>
																	{item.metadata?.postUrl
																		? '🚀 Published'
																		: '💾 Saved'}
																</Badge>
																{item.metadata?.templateName && (
																	<Badge
																		variant='outline'
																		className='bg-gradient-to-b from-blue-400 to-blue-600 text-white px-3 py-1'
																	>
																		{item.metadata.templateName}
																	</Badge>
																)}
																<BrandVoicePill brandVoice={item.brandVoice} />
															</div>
															<div className='text-gray-400'>
																{new Date(item.createdAt).toLocaleDateString(
																	'en-US',
																	{
																		month: 'short',
																		day: 'numeric',
																		hour: '2-digit',
																		minute: '2-digit',
																	},
																)}
															</div>
															<div className='text-lg text-gray-400 truncate max-w-[750px]'>
																{item.content?.split('.')[0].slice(0, 100) +
																	'...'}
															</div>
															{item.metadata?.postUrl && (
																<>
																	<div className='text-sm text-gray-400 line-clamp-1 mt-1'>
																		<span className='font-semibold text-white mr-2'>
																			Published URL:
																		</span>
																		<a
																			href={item.metadata.postUrl}
																			target='_blank'
																			rel='noopener noreferrer'
																			className='text-blue-400 hover:text-blue-300 underline'
																		>
																			{item.metadata.postUrl}
																		</a>
																	</div>
																	<MiniPostAnalytics
																		postId={item.metadata.postId}
																		platform={contentMachineQuery.platform}
																		profileKey={contentMachineQuery.profileKey}
																		contentHistoryId={item._id}
																		className='mt-2'
																	/>
																</>
															)}
														</motion.div>
													),
												)
											) : (
												<p className='text-sm text-gray-500'>
													No history yet. Generate content to see history.
												</p>
											)}
										</motion.div>
									)}
								</div>
							</div>
						</div>
					</div>
					<ConfirmDialog
						isOpen={engineToDelete !== null}
						onClose={() => setEngineToDelete(null)}
						title={
							<div className='text-2xl font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-2 animate-pulse'>
								<span className='text-3xl'>🔌️</span> Delete Content Machine
							</div>
						}
						description={
							<div className='space-y-4 text-center'>
								<p className='text-lg text-zinc-600 dark:text-zinc-400'>
									Are you sure you want to delete{' '}
									<span className='font-bold text-pink-500'>
										{contentMachineQuery.name}
									</span>
									?
								</p>
								<div className='p-4 bg-gradient-to-b from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-900 rounded-lg shadow-inner'>
									<p className='text-sm text-zinc-500 dark:text-zinc-400'>
										This action cannot be undone.
										<br />
										All associated data will be permanently deleted.
									</p>
								</div>
							</div>
						}
						onConfirm={async () => {
							if (engineToDelete) {
								await deleteEngine({ id: engineToDelete });
								setEngineToDelete(null);
								navigate('/admin/content-machine');
								toast({
									title: '🗑️ Deleted',
									description: 'Content engine has been deleted successfully.',
									duration: 3000,
								});
							}
						}}
						confirmText='🗑️ Delete'
						confirmButtonClassName='bg-gradient-to-b from-red-400 to-pink-600 hover:from-red-500 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-200'
					/>
					<Dialog
						open={engineToRename !== null}
						onOpenChange={(open) => !open && setEngineToRename(null)}
					>
						<DialogContent>
							<DialogHeader>
								<DialogTitle>Rename Content Engine</DialogTitle>
							</DialogHeader>
							<Input
								placeholder='New name'
								value={engineToRename?.name ?? ''}
								onChange={(e) =>
									setEngineToRename((prev: any) =>
										prev ? { ...prev, name: e.target.value } : null,
									)
								}
							/>
							<DialogFooter>
								<Button
									variant='outline'
									onClick={() => setEngineToRename(null)}
								>
									Cancel
								</Button>
								<Button
									onClick={async () => {
										if (engineToRename) {
											await updateName({
												id: engineToRename.id,
												name: engineToRename.name,
											});
											setEngineToRename(null);
											toast({
												title: '✏️ Renamed',
												description:
													'Content engine has been renamed successfully.',
												duration: 3000,
											});
										}
									}}
									disabled={!engineToRename?.name.trim()}
								>
									Save
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
					<ConfirmDialog
						isOpen={showDisconnectConfirm}
						onClose={() => setShowDisconnectConfirm(false)}
						title={
							<div className='text-2xl font-bold text-zinc-700 dark:text-zinc-300 flex items-center gap-2 animate-pulse'>
								<span className='text-3xl'>🔌</span> Disconnect Profile
							</div>
						}
						description={
							<div className='space-y-4 text-center'>
								<p className='text-lg text-zinc-600 dark:text-zinc-400'>
									Are you sure you want to disconnect{' '}
									<span className='font-bold text-pink-500'>
										{selectedProfile?.profileTitle || 'this profile'}
									</span>
									?
								</p>
								<div className='p-4 bg-gradient-to-b from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-900 rounded-lg shadow-inner'>
									<p className='text-sm text-zinc-500 dark:text-zinc-400'>
										Don't worry! This will only unlink the profile from this
										Content Machine.
										<br /> The profile will still be available for future use.
									</p>
								</div>
							</div>
						}
						onConfirm={() => {
							handleProfileSelect(null);
							setShowDisconnectConfirm(false);
						}}
						confirmText='🔌 Disconnect'
						confirmButtonClassName='bg-gradient-to-b from-zinc-400 to-zinc-600 hover:from-zinc-500 hover:to-zinc-700 text-white shadow-lg hover:shadow-xl transition-all duration-200'
					/>
					{/* Error Display Area */}
					{error && (
						<div className='space-y-4'>
							<Alert
								variant='destructive'
								className='mb-4'
								style={{
									backgroundColor: '#FF8C00',
									color: 'white',
									border: '1px solid #FF6B00',
								}}
							>
								<AlertTitle>🔥 Error 🔥</AlertTitle>
								<AlertDescription style={{ whiteSpace: 'pre-line' }}>
									{error}
								</AlertDescription>
							</Alert>
							<div className='flex justify-end'>
								<Button
									variant='outline'
									size='sm'
									onClick={() => setError(null)}
									className='bg-gradient-to-b from-zinc-400 to-zinc-600 hover:from-zinc-500 hover:to-zinc-700 text-white shadow-lg hover:shadow-xl transition-all duration-200'
								>
									🧹 Clear Error
								</Button>
							</div>
						</div>
					)}
					<div className='h-px bg-gray-200 dark:bg-gray-800 my-16' />
					<div className='my-12'>
						<div className='flex items-center justify-between mb-8'>
							<div className='space-y-1'>
								<h3 className='text-3xl font-bold'>
									Content Machine Brand Voice
								</h3>
								<p className='text-sm text-gray-500'>
									Selected voice will be used for generating social media
									content
								</p>
							</div>
						</div>

						<Card>
							<CardHeader>
								<CardTitle className='text-pink-500 text-3xl font-bold'>
									Select Voice Style
								</CardTitle>
							</CardHeader>
							<CardContent>
								<RadioGroup
									value={selectedBrandVoice}
									onValueChange={async (value) => {
										// Get voice details for confirmation
										let voiceDetails;
										if (value === 'user') {
											voiceDetails = {
												value,
												name: userBrandVoice.name,
												description: userBrandVoice.description,
												emoji: userBrandVoice.emoji,
											};
										} else {
											const voice = BRAND_VOICES.find(
												(v: any) => v.id === value,
											);
											if (voice) {
												voiceDetails = {
													value,
													name: voice.name,
													description: voice.description,
													emoji: voice.emoji,
												};
											}
										}

										if (voiceDetails) {
											setBrandVoiceToConfirm(voiceDetails);
										}
									}}
									className='space-y-4'
								>
									{userBrandVoice && (
										<Label
											htmlFor='user'
											className={cn(
												brandVoiceStyles.base,
												selectedBrandVoice === 'user'
													? brandVoiceStyles.selected
													: brandVoiceStyles.unselected,
											)}
										>
											<RadioGroupItem
												value='user'
												id='user'
												className={brandVoiceStyles.radio}
											>
												<span className={brandVoiceStyles.radioIcon}>✨</span>
											</RadioGroupItem>
											<div className={brandVoiceStyles.content}>
												<div className='flex items-center gap-2 text-lg font-semibold'>
													<span className='text-2xl'>
														{userBrandVoice.emoji}
													</span>
													<span>{userBrandVoice.name}</span>
												</div>
												<div className='flex items-center justify-between'>
													<div className='flex-1 min-w-0 pr-4'>
														<p className='text-sm text-muted-foreground'>
															{userBrandVoice.description}
														</p>
														{isAdmin &&
															(!documents || documents.length === 0) && (
																<p className='text-xs text-orange-500 mt-1'>
																	Admin preview: This is what users see when
																	they have a Brand Voice
																</p>
															)}
													</div>
													<Button
														variant='outline'
														size='sm'
														onClick={(e) => {
															e.preventDefault();
															const analysis =
																documents && documents.length > 0
																	? connectedProfile?.writingAnalysis ||
																		'No analysis available'
																	: connectedProfile?.writingAnalysis ||
																		'No writing analysis available yet';
															setCurrentAnalysis(analysis);
															setViewingBrandVoice({
																id: 'user',
																name: userBrandVoice?.name || 'My Brand Voice',
																emoji: userBrandVoice?.emoji || '✨',
															});
															setShowAnalysisDialog(true);
														}}
														className='relative group bg-gradient-to-br from-purple-500/80 via-pink-500/80 to-purple-500/80 hover:from-purple-600 hover:via-pink-600 hover:to-purple-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap ml-4 h-[70px] px-6'
													>
														<span className='flex flex-col items-center gap-1.5'>
															<span className='flex items-center gap-1'>
																✨ View Brand Voice
															</span>
														</span>
													</Button>
												</div>
											</div>
										</Label>
									)}

									{BRAND_VOICES.map((voice: any) => (
										<Label
											key={voice.id}
											htmlFor={voice.id}
											className={cn(
												brandVoiceStyles.base,
												selectedBrandVoice === voice.id
													? brandVoiceStyles.selected
													: brandVoiceStyles.unselected,
											)}
										>
											<RadioGroupItem
												value={voice.id}
												id={voice.id}
												className={brandVoiceStyles.radio}
												disabled={isUpdatingBrandVoice}
											>
												<span className={brandVoiceStyles.radioIcon}>
													{isUpdatingBrandVoice ? '⏳' : '✨'}
												</span>
											</RadioGroupItem>
											<div className={brandVoiceStyles.content}>
												<div className='flex items-center gap-2 text-lg font-semibold'>
													<span className='text-2xl'>{voice.emoji}</span>
													<span>{voice.name}</span>
												</div>
												<div className='flex items-center justify-between'>
													<div className='flex-1 min-w-0 pr-4'>
														<p className='text-sm text-muted-foreground'>
															{voice.description}
														</p>
														<p className='text-xs text-gray-500'>
															Style: {voice.style}
														</p>
													</div>
													<Button
														variant='outline'
														size='sm'
														onClick={(e) => {
															e.preventDefault();
															let analysis = '';
															if (voice.id === 'prometheus') {
																analysis =
																	'🔥 Prometheus is our default AI voice, designed to create engaging social media content with a mix of professionalism and personality.';
															} else if (voice.id === 'brent') {
																analysis =
																	brentProfile?.writingAnalysis ||
																	'No analysis available';
															} else if (voice.id === 'reverend') {
																analysis =
																	reverendProfile?.writingAnalysis ||
																	'No analysis available';
															}
															setCurrentAnalysis(analysis);
															setViewingBrandVoice({
																id: voice.id,
																name: voice.name,
																emoji: voice.emoji,
															});
															setShowAnalysisDialog(true);
														}}
														className='relative group bg-gradient-to-br from-purple-500/80 via-pink-500/80 to-purple-500/80 hover:from-purple-600 hover:via-pink-600 hover:to-purple-600 text-white border-0 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 whitespace-nowrap ml-4 h-[70px] px-6'
													>
														<span className='flex flex-col items-center gap-1.5'>
															<span className='flex flex-col items-center gap-1.5'>
																<span className='flex items-center gap-1'>
																	✨ View Brand Voice
																</span>
															</span>
														</span>
													</Button>
												</div>
											</div>
										</Label>
									))}
								</RadioGroup>
							</CardContent>
						</Card>
					</div>
					{/* Add the Brand Voice Dialogs */}
					<Dialog
						open={showAnalysisDialog}
						onOpenChange={(open) => {
							if (!open) {
								setViewingBrandVoice(null);
							}
							setShowAnalysisDialog(open);
						}}
					>
						<DialogContent className='max-w-2xl max-h-[80vh] overflow-hidden'>
							<div className='flex flex-col h-[calc(80vh-2rem)]'>
								<DialogHeader className='flex-none bg-background py-4 border-b'>
									<DialogTitle className='text-2xl font-bold flex items-center gap-2 pr-8'>
										{viewingBrandVoice?.emoji} {viewingBrandVoice?.name} - Brand
										Voice Analysis
									</DialogTitle>
								</DialogHeader>

								<div className='flex-1 overflow-y-auto'>
									<div className='p-6 bg-gray-50 dark:bg-gray-900 rounded-lg'>
										<div className='prose dark:prose-invert max-w-none'>
											<ReactMarkdown
												components={
													{
														p: ({
															children,
														}: {
															children: React.ReactNode;
														}) => (
															<p className='mb-4 text-gray-600 dark:text-gray-300'>
																{children}
															</p>
														),
														strong: ({
															children,
														}: {
															children: React.ReactNode;
														}) => (
															<strong className='font-bold text-gray-900 dark:text-white'>
																{children}
															</strong>
														),
														em: ({
															children,
														}: {
															children: React.ReactNode;
														}) => (
															<em className='text-pink-600 dark:text-pink-400 not-italic font-medium'>
																{children}
															</em>
														),
													} as any
												}
											>
												{currentAnalysis || ''}
											</ReactMarkdown>
										</div>
									</div>
								</div>
							</div>
						</DialogContent>
					</Dialog>
					<Dialog
						open={!!brandVoiceToConfirm}
						onOpenChange={() => setBrandVoiceToConfirm(null)}
					>
						<DialogContent className='max-w-md'>
							<DialogHeader>
								<DialogTitle className='text-2xl flex items-center gap-2'>
									{brandVoiceToConfirm?.emoji} Change Brand Voice?
								</DialogTitle>
							</DialogHeader>

							<div className='py-4'>
								<h3 className='text-lg font-semibold mb-2 flex items-center gap-2'>
									{brandVoiceToConfirm?.emoji} {brandVoiceToConfirm?.name}
								</h3>
								<p className='text-gray-600 dark:text-gray-300 mb-4'>
									{brandVoiceToConfirm?.description}
								</p>

								{brandVoiceToConfirm?.value === 'reverend' && (
									<div className='p-4 bg-orange-100 dark:bg-orange-900/20 rounded-lg border-2 border-orange-300 dark:border-orange-700'>
										<div className='font-semibold text-orange-800 dark:text-orange-200 flex items-center gap-2'>
											⚠️ Warning:
										</div>
										<div className='text-orange-700 dark:text-orange-300 mt-2'>
											Heads up! The Reverend doesn't hold back and might drop
											some 🤬 curse words in the content. Perfect for edgy
											content, but maybe not for your mom's cooking blog! 😅
										</div>
									</div>
								)}
							</div>

							<DialogFooter className='flex gap-2'>
								<Button
									variant='outline'
									onClick={() => setBrandVoiceToConfirm(null)}
								>
									Cancel
								</Button>
								<Button
									className={cn(
										'text-white',
										brandVoiceToConfirm?.value === 'reverend'
											? 'bg-orange-500 hover:bg-orange-600'
											: 'bg-pink-500 hover:bg-pink-600',
									)}
									onClick={async () => {
										if (!brandVoiceToConfirm) return;

										try {
											setIsUpdatingBrandVoice(true);
											const value = brandVoiceToConfirm.value;

											let profileId: Id<'profiles'> | undefined;
											switch (value) {
												case 'user':
													profileId = adminPreviewProfile?._id;
													break;
												case 'brent':
													profileId = brentProfile?._id;
													break;
												case 'reverend':
													profileId = reverendProfile?._id;
													break;
												case 'prometheus':
													profileId = undefined;
													break;
											}

											await updateBrandVoice({
												contentMachineId: contentMachineQuery._id,
												brandVoice: {
													id: value,
													profileId,
												},
											});

											setSelectedBrandVoice(value);
											setBrandVoiceToConfirm(null);

											toast({
												title: '🎭 Brand Voice Updated',
												description:
													value === 'prometheus' ? (
														<>
															Using <strong>🔥 Prometheus</strong>, the default
															AI voice for content generation
														</>
													) : (
														<>
															Content Machine will now use{' '}
															<strong>
																{brandVoiceToConfirm.emoji}{' '}
																{brandVoiceToConfirm.name}
															</strong>{' '}
															for generating content
														</>
													),
												duration: 3000,
											});
										} catch (error) {
											console.error('Detailed error:', error);
											toast({
												title: 'Error',
												description:
													error instanceof Error
														? error.message
														: 'Failed to update brand voice',
												variant: 'destructive',
												duration: 3000,
											});
											setSelectedBrandVoice(
												contentMachineQuery.brandVoice?.id || 'prometheus',
											);
										} finally {
											setIsUpdatingBrandVoice(false);
										}
									}}
								>
									{brandVoiceToConfirm?.value === 'reverend'
										? "🎭 Let's Get Spicy!"
										: '✨ Confirm Change'}
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
					<ConfirmDialog
						isOpen={showDeleteContentConfirm}
						onClose={() => setShowDeleteContentConfirm(false)}
						title={
							<div className='flex flex-col items-center justify-center py-8 space-y-4'>
								<img src='/logo.png' alt='Prometheus' className='h-12 mb-4' />
								<motion.div
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									className='text-xl font-semibold text-purple-500'
								>
									Delete Generated Content
								</motion.div>
								<div className='text-sm text-gray-400 text-center'>
									✨ Are you sure you want to delete this content? ✨
								</div>
							</div>
						}
						description={
							<div className='p-4 bg-gradient-to-b from-zinc-100 to-zinc-200 dark:from-zinc-800 dark:to-zinc-900 rounded-lg shadow-inner'>
								<p className='text-lg text-zinc-400 dark:text-zinc-400 text-center'>
									⚡️ This will clear the current content and
									<br />
									allow you to generate new content.
									<br />
									This action cannot be undone.⚡️
								</p>
							</div>
						}
						onConfirm={async () => {
							try {
								await deleteContent({
									id: contentMachineQuery._id,
								});
								setNextContent('');
								setBuzzUrl('');
								setShowDeleteContentConfirm(false);
								if (selectedTemplate === '' || selectedTemplate === 'random') {
									pickNewRandomTemplate();
								}
								toast({
									title: '🗑️ Content Deleted',
									description: 'Generated content has been cleared.',
									duration: 3000,
								});
							} catch (error) {
								console.error('Error deleting content:', error);
								toast({
									title: 'Error',
									description:
										error instanceof Error
											? error.message
											: 'Failed to delete content',
									variant: 'destructive',
									duration: 3000,
								});
							}
						}}
						confirmText='🗑️ Delete'
						confirmButtonClassName='bg-gradient-to-b from-red-400 to-pink-600 hover:from-red-500 hover:to-pink-700 text-white shadow-lg hover:shadow-xl transition-all duration-200'
					/>
				</CardContent>
			</Card>

			<GeneratingDialog
				isOpen={isRunning}
				onOpenChange={setIsRunning}
				platform={contentMachineQuery.platform as 'twitterx' | 'linkedin'}
			/>

			{/* Add the publish dialog */}
			<Dialog
				open={showPublishDialog}
				onOpenChange={(open) => {
					if (!isPublishing) {
						setShowPublishDialog(open);
					}
				}}
			>
				<DialogContent className='sm:max-w-md'>
					<DialogHeader>
						<DialogTitle className='sr-only'>Publishing Options</DialogTitle>
					</DialogHeader>
					<div className='flex flex-col items-center justify-center py-8 space-y-4'>
						<img src='/logo.png' alt='Prometheus' className='h-12 mb-4' />
						<motion.div
							initial={{ opacity: 0, y: 10 }}
							animate={{ opacity: 1, y: 0 }}
							className='text-xl font-semibold text-purple-500'
						>
							Ready to Share
						</motion.div>
						<div className='text-sm text-gray-400 text-center'>
							✨ Choose how you'd like to share your content ✨
						</div>
						<div className='flex w-full gap-3'>
							<Button
								variant='default'
								onClick={handlePublishNow}
								disabled={isPublishing}
								className={cn(
									'flex-1 whitespace-nowrap bg-gradient-to-r from-pink-500 via-purple-500 to-pink-500 text-white py-6',
									!isPublishing &&
										'hover:from-pink-600 hover:via-purple-600 hover:to-pink-600',
									isPublishing && 'opacity-80',
								)}
							>
								{isPublishing ? (
									<div className='flex items-center justify-center gap-2'>
										<Loader2 className='h-4 w-4 animate-spin' />
										Publishing...
									</div>
								) : (
									'🚀 Publish Now'
								)}
							</Button>
							<Button
								onClick={handleSchedule}
								disabled
								className={cn(
									'flex-1 whitespace-nowrap bg-gradient-to-r from-blue-500 via-indigo-500 to-blue-500 text-white py-6',
									'opacity-70',
								)}
							>
								Schedule Unavailable
							</Button>
						</div>
					</div>
				</DialogContent>
			</Dialog>

			{/* Add the publish success dialog */}
			<PublishSuccessDialog
				isOpen={showPublishSuccess}
				onClose={handlePublishSuccessClose}
				postUrl={publishedPostUrl}
				templateName={publishedTemplateName}
				machineId={contentMachineQuery._id}
				buzzUrl={buzzUrl}
			/>
		</div>
	);
}
